import PropTypes from 'prop-types';
import Spinner from '@scalero-inc/forno-spinner';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { FiXCircle } from 'react-icons/fi';
import { jsxs, jsx } from 'react/jsx-runtime';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var sizes = ['xs', 's', 'm', 'l'];
var themes = ['light', 'dark'];
var variants = ['normal', 'subtle'];
var statuses = ['normal', 'danger'];
var hierarchies = ['primary', 'secondary', 'tertiary'];

var _excluded = ["className", "size", "theme", "label", "prefix", "sufix", "loading", "disabled", "helperText", "warningText", "hierarchy", "type", "status", "variant"];
var prefix = 'forno-input';
/**
 * @param {Object} props
 * @param	{string} [props.className=] - Custom class name
 * @param	{string} [props.id=] - Id for the label
 * @param	{string} [props.size=m] - [s,m]
 * @param	{string} [props.theme=light] - [light,dark]
 * @param	{Object} [props.label=] - Label related to the input
 * @param	{Object} [props.prefix=] - Svg Icon to prefix the input
 * @param	{Object} [props.sufix=] - Svg Icon to prefix the input
 * @param	{string} [props.disabled=false] - disabled state
 * @param	{string} [props.helperText=] - Helper text
 * @param	{boolean} [props.warningText=] - Warning text
 * @param	{boolean} [props.variant=normal] - [normal, subtle]
 * @param {string} [props.status=normal] - [normal, danger]
 * @param {string} [props.hierarchy=tertiary] - [primary, secondary, tertiary, neutral]
 */

var Input = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
      size = _ref.size,
      theme = _ref.theme,
      label = _ref.label,
      Prefix = _ref.prefix,
      Sufix = _ref.sufix,
      loading = _ref.loading,
      disabled = _ref.disabled,
      helperText = _ref.helperText,
      warningText = _ref.warningText,
      hierarchy = _ref.hierarchy,
      type = _ref.type,
      status = _ref.status,
      variant = _ref.variant,
      rest = _objectWithoutProperties(_ref, _excluded);

  var id = rest.id;
  var typography = size === 'xs' ? 'font-block-s' : "font-block-".concat(size);
  var labelTypography = size === 'l' ? 'font-block-m' : "font-block-s";
  var customClassName = classNames("".concat(prefix, "-wrapper"), className, typography);
  var customLabelClassName = classNames("".concat(prefix, "-label"), labelTypography);
  var helperLabelClassName = classNames("".concat(prefix, "-helper-text"), labelTypography);
  var warningLabelClassName = classNames("".concat(prefix, "-warning-text"), labelTypography);
  var cancelIconClassName = classNames('forno-input-cancel-icon', "".concat(prefix, "-icon"));
  return /*#__PURE__*/jsxs("div", {
    className: "".concat(prefix, "-container"),
    "data-theme": theme,
    children: [/*#__PURE__*/jsxs("div", {
      className: customClassName,
      "data-sufix": Sufix !== null || loading,
      "data-prefix": Prefix !== null,
      "data-size": size,
      "data-loading": loading,
      "data-disabled": disabled,
      "data-variant": variant,
      "data-status": status,
      children: [label ? /*#__PURE__*/jsx("label", {
        htmlFor: id,
        className: customLabelClassName,
        children: label
      }) : null, Prefix ? /*#__PURE__*/jsx(Prefix, {
        className: "".concat(prefix, "-prefix ").concat(prefix, "-icon")
      }) : null, /*#__PURE__*/jsx("input", _objectSpread2(_objectSpread2({
        className: prefix
      }, rest), {}, {
        ref: ref,
        "data-size": size,
        "data-theme": theme,
        disabled: disabled,
        type: type
      })), !loading && Sufix ? /*#__PURE__*/jsx("div", {
        className: "".concat(prefix, "-sufix ").concat(prefix, "-icon"),
        children: /*#__PURE__*/jsx(Sufix, {})
      }) : null, loading ? /*#__PURE__*/jsx("div", {
        className: "".concat(prefix, "-sufix-spinner ").concat(prefix, "-icon"),
        children: /*#__PURE__*/jsx(Spinner, {
          className: "".concat(prefix, "-loading-icon"),
          size: size === 'xs' ? 's' : size,
          hierarchy: hierarchy
        })
      }) : null, type === 'search' ? /*#__PURE__*/jsx(FiXCircle, {
        className: cancelIconClassName
      }) : null]
    }), helperText ? /*#__PURE__*/jsx("p", {
      className: helperLabelClassName,
      children: helperText
    }) : null, warningText ? /*#__PURE__*/jsx("p", {
      className: warningLabelClassName,
      children: warningText
    }) : null]
  });
});
Input.displayName = 'Input';
Input.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  size: PropTypes.oneOf(sizes),
  theme: PropTypes.oneOf(themes),
  prefix: PropTypes.func,
  sufix: PropTypes.func,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  helperText: PropTypes.string,
  warningText: PropTypes.string,
  hierarchy: PropTypes.oneOf(hierarchies),
  type: PropTypes.string,
  status: PropTypes.oneOf(statuses),
  variant: PropTypes.oneOf(variants)
};
Input.defaultProps = {
  className: null,
  id: null,
  label: null,
  size: 'm',
  theme: 'light',
  prefix: null,
  sufix: null,
  loading: false,
  disabled: false,
  helperText: null,
  warningText: null,
  type: 'text',
  status: 'normal',
  hierarchy: 'tertiary',
  variant: 'normal'
};

export { Input as default, prefix };
